import { withApollo } from 'react-apollo'

import { withAnalyticsClient } from '@acorns/web-utils'
import { datadogRum } from '@datadog/browser-rum'
import { withFormik } from 'formik'
import debounce from 'lodash-es/debounce'
import { Try } from 'monapt'
import { pathOr } from 'ramda'
import { compose, withHandlers, withProps } from 'recompose'

import { withTrackClickedHandler } from 'components/trackers'
import withWebView from 'pages/spend/direct-deposit/withWebView'
import { USDFormatter } from 'utils/number/currency'

import { employerInfoRows, validationSchema } from '../employer-form'
import { initialValues, withDirectDepositPdf } from '../enhance'
import generateUpdatedPDF from '../generateUpdatedPdf.gql'
import { BackButtonTapped } from '../segment'
import { WVProps } from '../types'

const DEBOUNCE_TIMEOUT = 10000

export const handleSubmit = () => {
  window.location.assign(
    'acorns://event?flow_complete=true&error=false&email=true',
  )
}

const enhance = compose<WVProps, {}>(
  withTrackClickedHandler(BackButtonTapped),
  withWebView,
  withDirectDepositPdf,
  withAnalyticsClient,
  withApollo,
  withProps(({ data, location }) => {
    if (!data.loading) {
      return {
        employerInfoRows: employerInfoRows(),
        userId: pathOr('', ['user', 'uuid'], data),
        employerName: pathOr('', ['state', 'employerName'], location),
        bankAccountId: pathOr('', ['spendAccounts', 0, 'id'], data),
        balance: USDFormatter.format(
          pathOr(0, ['spendAccounts', 0, 'balance', 'value'], data),
        ),
        hasDirectDepositFlowImprovements: true,
        hasMobileDirectDepositJSBridge: !!(
          window?.webkit?.messageHandlers?.sharePdf ||
          window?.JSInterface?.sharePdf
        ),
      }
    } else {
      return {
        employerName: pathOr('', ['state', 'employerName'], location),
      }
    }
  }),
  withFormik({
    mapPropsToValues: (props) => {
      return initialValues(props)
    },
    isInitialValid: (props) => {
      const values = initialValues(props)
      return values.name && values.name.length > 0
    },
    enableReinitialize: true,
    validationSchema,
    handleSubmit,
  }),
  withHandlers({
    handleEmailMyPDF:
      ({
        emailPdf,
        bankAccountId,
        values,
        setProcessing,
        mobileSessionId,
        client,
        userId,
        analytics,
        hasDirectDepositFlowImprovements,
        hasMobileDirectDepositJSBridge,
      }) =>
      async () => {
        setProcessing(true)
        if (
          hasDirectDepositFlowImprovements &&
          hasMobileDirectDepositJSBridge
        ) {
          return new Promise((resolve) => {
            client
              .query({
                query: generateUpdatedPDF,
                variables: {
                  bankAccountId,
                  userId,
                  employerName: values.name.trim(),
                  sessionId: analytics.getContext().sessionId,
                },
              })
              .then(
                (response) => {
                  setProcessing(false)
                  if (window?.webkit?.messageHandlers) {
                    // iOS
                    debounce(
                      window.webkit.messageHandlers.sharePdf.postMessage(
                        response.data.generateUpdatedPdf.pdf,
                      ),
                      DEBOUNCE_TIMEOUT,
                    )
                  } else if (window?.JSInterface) {
                    // android
                    debounce(
                      window.JSInterface.sharePdf(
                        response.data.generateUpdatedPdf.pdf,
                      ),
                      DEBOUNCE_TIMEOUT,
                    )
                  }
                  resolve(true)
                },
                (err) => {
                  if (window?.webkit?.messageHandlers) {
                    window.webkit.messageHandlers.sharePdf.postMessage(null)
                  } else if (window?.JSInterface) {
                    window.JSInterface.sharePdf(null)
                  }
                  datadogRum.addError(err)
                  setProcessing(false)
                  resolve(false)
                },
              )
          })
        } else {
          return new Promise((resolve) => {
            emailPdf({
              variables: {
                name: values.name,
                bankAccountId,
                sessionId: mobileSessionId,
              },
            }).then(
              () => {
                window.location.assign(
                  'acorns://event?flow_complete=true&error=false&email=true',
                )
                setProcessing(false)
                resolve(true)
              },
              (err) => {
                datadogRum.addError(err)
                window.location.assign(
                  'acorns://event?flow_complete=true&error=true&email=true',
                )
                setProcessing(false)
                resolve(false)
              },
            )
          })
        }
      },
    handleChange:
      ({ setFieldValue }) =>
      (e) => {
        // Default ascii chars only. This is to prevent users from entering in emojis.
        setFieldValue(
          e.target.name,
          // eslint-disable-next-line no-control-regex
          e.target.value.replace(/[^\x00-\x7F]/g, ''),
        )
      },
    handleSelectChange:
      ({ setFieldValue }) =>
      (field, value) => {
        setFieldValue(field, value)
      },
    handleWebViewBack:
      ({ trackClick, history, employerName }) =>
      () => {
        Try(() => trackClick())
        employerName !== undefined
          ? history.push('/wv/spend/direct-deposit/search')
          : window.location.assign(
              'acorns://event?flow_complete=true&error=false&email=false',
            )
      },
  }),
)

export default enhance
